<template>
  <div class="card card-plain">
    <div class="card-body">
      <h6 class="font-weight-bold mb-1">AI Prompt Settings</h6>
      <p class="text-xs text-secondary mb-3">
        Edit prompts used by Gemini. Use <code v-pre>{{variable}}</code> placeholders (e.g. <code v-pre>{{content}}</code>, <code v-pre>{{context}}</code>).
        API key is configured in <code>backend/.env</code> only.
      </p>

      <div v-if="loading" class="text-secondary text-sm py-3">
        <i class="fas fa-spinner fa-spin me-1"></i> Loading prompts...
      </div>
      <div v-else-if="error" class="alert alert-danger text-sm py-2">{{ error }}</div>

      <div v-else class="row g-3">
        <div class="col-12 col-lg-4">
          <div class="list-group prompt-key-list">
            <button
              v-for="p in prompts"
              :key="p.key"
              type="button"
              class="list-group-item list-group-item-action text-sm"
              :class="{ active: selectedKey === p.key }"
              @click="selectPrompt(p.key)"
            >
              <div class="font-weight-bold">{{ p.label || p.key }}</div>
              <div class="text-xs opacity-8">{{ p.key }}</div>
            </button>
          </div>
        </div>

        <div v-if="form" class="col-12 col-lg-8">
          <div class="border rounded p-3 bg-white">
            <div class="mb-2">
              <label class="text-xs font-weight-bold text-secondary">Label</label>
              <input v-model="form.label" type="text" class="form-control form-control-sm" />
            </div>
            <div class="mb-2">
              <label class="text-xs font-weight-bold text-secondary">Description</label>
              <input v-model="form.description" type="text" class="form-control form-control-sm" />
            </div>
            <div class="row g-2 mb-2">
              <div class="col-6">
                <label class="text-xs font-weight-bold text-secondary">Model (optional)</label>
                <input v-model="form.model" type="text" class="form-control form-control-sm" placeholder="gemini-2.0-flash" />
              </div>
              <div class="col-3">
                <label class="text-xs font-weight-bold text-secondary">Max tokens</label>
                <input v-model.number="form.maxOutputTokens" type="number" min="256" class="form-control form-control-sm" />
              </div>
              <div class="col-3">
                <label class="text-xs font-weight-bold text-secondary">Format</label>
                <base-select
                  v-model="form.responseFormat"
                  size="sm"
                  :options="responseFormatOptions"
                  option-label="label"
                  option-value="value"
                />
              </div>
            </div>
            <div class="mb-3">
              <label class="text-xs font-weight-bold text-secondary">Prompt template</label>
              <textarea v-model="form.userTemplate" class="form-control form-control-sm font-monospace" rows="14" />
            </div>
            <div class="d-flex justify-content-end gap-2">
              <button type="button" class="btn btn-sm btn-outline-secondary mb-0" @click="reload">Reset</button>
              <button type="button" class="btn btn-sm bg-gradient-primary mb-0" :disabled="saving" @click="save">
                <span v-if="saving"><i class="fas fa-spinner fa-spin me-1"></i></span>
                Save prompt
              </button>
            </div>
            <p v-if="saveMsg" class="text-xs text-success mb-0 mt-2">{{ saveMsg }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { aiApi } from "@/services/backendApi";

export default {
  name: "AiPromptSetting",
  data() {
    return {
      loading: false,
      saving: false,
      error: null,
      saveMsg: "",
      prompts: [],
      selectedKey: "",
      form: null,
    };
  },
  async created() {
    await this.reload();
  },
  computed: {
    responseFormatOptions() {
      return [
        { label: "text", value: "text" },
        { label: "json", value: "json" },
      ];
    },
  },
  methods: {
    async reload() {
      this.loading = true;
      this.error = null;
      this.saveMsg = "";
      try {
        this.prompts = await aiApi.listPrompts();
        if (!this.selectedKey && this.prompts.length) {
          this.selectPrompt(this.prompts[0].key);
        } else if (this.selectedKey) {
          this.selectPrompt(this.selectedKey);
        }
      } catch (e) {
        this.error = e.message || "Failed to load prompts";
        this.prompts = [];
        this.form = null;
      } finally {
        this.loading = false;
      }
    },
    selectPrompt(key) {
      this.selectedKey = key;
      const row = this.prompts.find((p) => p.key === key);
      if (!row) {
        this.form = null;
        return;
      }
      this.form = {
        label: row.label || "",
        description: row.description || "",
        model: row.model || "",
        maxOutputTokens: row.maxOutputTokens || 1024,
        responseFormat: row.responseFormat || "text",
        userTemplate: row.userTemplate || "",
      };
      this.saveMsg = "";
    },
    async save() {
      if (!this.selectedKey || !this.form) return;
      this.saving = true;
      this.saveMsg = "";
      this.error = null;
      try {
        await aiApi.updatePrompt(this.selectedKey, {
          label: this.form.label,
          description: this.form.description,
          model: this.form.model || null,
          maxOutputTokens: this.form.maxOutputTokens,
          responseFormat: this.form.responseFormat,
          userTemplate: this.form.userTemplate,
        });
        this.saveMsg = "Saved.";
        await this.reload();
      } catch (e) {
        this.error = e.message || "Save failed";
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.prompt-key-list .list-group-item {
  border-radius: 8px;
  margin-bottom: 4px;
}
.font-monospace {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.72rem;
}
</style>
